import { type JSX } from 'solid-js'

/**
 * Os icones das metricas, desenhados a mao.
 *
 * Nao ha biblioteca aqui, e e escolha: sao oito formas, cada uma usada uma vez, e
 * puxar um pacote inteiro por isso custaria mais em bytes do que o desenho custa em
 * linhas. O painel usa Material Design Icons porque la sao dezenas e precisam
 * combinar com o painel antigo; aqui nao ha com o que combinar.
 *
 * Todos em `stroke` de 1.5 sobre uma grade de 24, que e o que os faz parecerem da
 * mesma familia mesmo desenhados um a um.
 */

type Props = { class?: string }

function Base(props: { class?: string; children: JSX.Element }): JSX.Element {
    return (
        <svg
            class={props.class}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
        >
            {props.children}
        </svg>
    )
}

const Sleep = (p: Props) => (
    <Base class={p.class}>
        <path d="M20.5 14.5A8.5 8.5 0 0 1 9.5 3.5a8.5 8.5 0 1 0 11 11Z" />
    </Base>
)

const Heart = (p: Props) => (
    <Base class={p.class}>
        <path d="M3 12h3l2-4 3 8 2.5-6 1.8 3h5.7" />
        <path d="M20.8 8.6a4.6 4.6 0 0 0-8.8-2 4.6 4.6 0 0 0-8.6 2.2" />
    </Base>
)

const Hrv = (p: Props) => (
    <Base class={p.class}>
        <path d="M3 17.5 7 8l3.5 8L14 5l3 10 3.5-5" />
    </Base>
)

const Pressure = (p: Props) => (
    <Base class={p.class}>
        <circle cx="12" cy="13" r="7" />
        <path d="M12 13V9M9 3h6" />
    </Base>
)

const Oxygen = (p: Props) => (
    <Base class={p.class}>
        <path d="M12 3s5.5 5.4 5.5 9.3A5.5 5.5 0 0 1 12 18a5.5 5.5 0 0 1-5.5-5.7C6.5 8.4 12 3 12 3Z" />
        <path d="M12 21v-3" />
    </Base>
)

const Temperature = (p: Props) => (
    <Base class={p.class}>
        <path d="M10 13.5V5a2 2 0 1 1 4 0v8.5a4 4 0 1 1-4 0Z" />
        <path d="M12 16.5v-5" />
    </Base>
)

const Steps = (p: Props) => (
    <Base class={p.class}>
        <path d="M7 4c1.7 0 2.6 1.4 2.6 3.2 0 1.6-.6 2.6-.6 4 0 1.1.8 1.6.8 2.8 0 1.4-1.1 2.3-2.6 2.3s-2.7-.9-2.7-2.4c0-1.3.8-1.8.8-2.9 0-1.3-.7-2.3-.7-3.8C4.6 5.4 5.4 4 7 4Z" />
        <path d="M16.8 8.3c1.5 0 2.4 1.3 2.4 3 0 1.5-.6 2.4-.6 3.7 0 1 .7 1.5.7 2.6 0 1.3-1 2.1-2.4 2.1s-2.5-.8-2.5-2.2c0-1.2.7-1.7.7-2.7 0-1.2-.6-2.1-.6-3.5 0-1.8.8-3 2.3-3Z" />
    </Base>
)

const Energy = (p: Props) => (
    <Base class={p.class}>
        <path d="M13 3 5.5 13.5H11L10 21l7.5-10.5H12L13 3Z" />
    </Base>
)

/** Do nome que o scope publica para a forma. */
const SHAPES: Record<string, (p: Props) => JSX.Element> = {
    sleep: Sleep,
    heart: Heart,
    hrv: Hrv,
    pressure: Pressure,
    oxygen: Oxygen,
    temperature: Temperature,
    steps: Steps,
    energy: Energy
}

export function MetricIcon(props: { name: string; class?: string }): JSX.Element {
    const Shape = SHAPES[props.name] ?? Heart
    return <Shape class={props.class} />
}

/**
 * A marca: o simbolo Mormaii, do SVG oficial que o cliente entregou em 30/09/2026
 * (`demandas/design/LOGOS MORMAII/PRETA.svg`).
 *
 * O desenho e o do arquivo, sem retoque; so o viewBox foi recortado para um quadrado
 * justo em volta do simbolo (o original tem ~12% de respiro em cada lado, e ele ficava
 * miudo nos 28px da barra). A cor NAO vem do arquivo: e `currentColor`, para cada lugar
 * seguir com a cor que ja usava - o verde-agua `#00AFAA` da marca nao foi adotado.
 */
export function Brand(props: Props): JSX.Element {
    return (
        <svg class={props.class} viewBox="111 126 770 770" aria-hidden="true">
            <path
                fill="currentColor"
                d="M153.024 721.764C171.928 703.939 190.834 686.118 209.743 668.299C214.995 663.356 212.806 665.413 207.585 661.475C201.752 659.962 195.919 658.453 190.084 656.948C210.461 548.789 330.004 411.615 401.71 422.492C419.088 424.414 435.433 431.701 448.472 443.339C529.591 366.883 610.718 290.432 691.853 213.987C652.971 213.392 601.522 227.037 545.834 252.275C484.254 280.764 426.584 317.027 374.236 360.176C318.293 405.984 266.373 460.777 226.737 521.911C187.1 583.045 159.783 650.548 153.024 721.764ZM245.237 822.873C265.044 806.039 284.839 789.205 304.623 772.369C310.129 767.703 307.837 769.645 311.213 775.253C312.09 781.196 312.967 787.141 313.844 793.088C423.904 784.473 573.528 680.686 570.366 608.446C570.298 591.007 564.777 574.026 554.575 559.876C639.523 487.658 724.467 415.436 809.408 343.21C805.82 381.81 786.703 431.345 755.565 483.838C720.541 541.851 678.203 595.127 629.591 642.357C577.892 692.898 517.675 738.481 452.462 771.235C387.248 803.989 316.989 823.822 245.231 822.878M117.516 806.959L778.658 179.165C809.389 169.385 846.585 165.806 874.252 163.732C868.594 192.387 862.602 224.395 849.157 256.953L143.815 858.549C142.495 831.74 138.872 820.19 117.507 806.941"
            />
        </svg>
    )
}
